import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { Tooltip } from 'uikit';

import type React from 'react';
import { useContext, useMemo } from 'react';

import type { ItemAction } from '../../DropdownContext';
import { DropdownContext } from '../../DropdownContext';
import type { ITableLikeTreeItem } from '../context';
import { TableLikeTreeContext } from '../context';
import { useTableTreeDrop } from '../hooks/useTableTreeDrop';

import { DragButton } from './DragButton';
import { TreeItemCheckBox } from './TreeItemCheckBox';

import Style from './MenuColumnCell.module.css';

interface IMenuColumnCell {
    menuItems?: ItemAction[];
    menuItemsGetter?: (treeItem: ITableLikeTreeItem, isChecked: boolean) => ItemAction[];
    treeItem: ITableLikeTreeItem;
    isContextMenuAvailable?: boolean;
    isCheckboxAvailable?: boolean;
    columnsCount: number;
    isDragAndDropDisabled?: boolean;
    isDragButtonHidden?: boolean;
    isDragButtonObjHidden?: boolean;
    isAllMenuDisabledCallback?: (treeItem: ITableLikeTreeItem, isChecked?: boolean) => boolean;
}

export const MenuColumnCell: React.FC<IMenuColumnCell> = (props) => {
    const {
        menuItems,
        menuItemsGetter,
        treeItem,
        isContextMenuAvailable,
        isCheckboxAvailable,
        columnsCount,
        isDragAndDropDisabled,
        isDragButtonHidden,
        isDragButtonObjHidden,
        isAllMenuDisabledCallback,
    } = props;

    const ref = useTableTreeDrop({ treeItem, columnsCount, isDragButtonHidden });
    const context = useContext(TableLikeTreeContext);

    const currentMenuItems = useMemo(() => {
        if (menuItemsGetter) {
            return menuItemsGetter(treeItem, context.checkedIds.includes(treeItem.id));
        }

        return menuItems || [];
    }, [context.checkedIds, menuItems, menuItemsGetter, treeItem]);

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    const isDragDisabled = useMemo(
        () => isDragAndDropDisabled || context?.dragOperationDisabledItemsIds?.includes(treeItem.id),
        [context?.dragOperationDisabledItemsIds, isDragAndDropDisabled, treeItem.id],
    );

    const isDropdownDisabled = useMemo(
        () => !!isAllMenuDisabledCallback?.(treeItem),
        [isAllMenuDisabledCallback, treeItem],
    );

    const hiddenObj = isDragButtonObjHidden && treeItem.treeLevel === 0;

    return (
        <div className={Style.menuColumnCell} ref={!isDragButtonHidden ? ref : null}>
            {!isDragButtonHidden && !hiddenObj && (
                <DragButton treeItem={treeItem} isDragAndDropDisabled={isDragDisabled} />
            )}
            {isCheckboxAvailable && <TreeItemCheckBox treeItem={treeItem} />}
            {isContextMenuAvailable && !!currentMenuItems?.length && (
                <DropdownContext
                    items={currentMenuItems}
                    wrapperClassName={`${Style.menuColumnCellContextMenu} ${hiddenObj ? Style.onlyContextMenu : ''} `}
                    context={{ context, item: treeItem }}
                    getItemAttributesHandler={getMenuItemAttributes}
                    onMouseMoveHandler={onMenuMouseMove}
                    onMouseLeaveHandler={onMenuMouseLeave}
                    isDisabled={isDropdownDisabled}
                />
            )}
            {activeTooltipMsg && (
                <Tooltip
                    size={activeTooltipMsg.length > 80 ? 'm' : 's'}
                    direction="rightCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
        </div>
    );
};

MenuColumnCell.displayName = 'MenuColumnCell';
